import cx from 'classnames'
import { asField } from 'informed'
import { Range, RangeProps } from 'rc-slider'
import * as React from 'react'

import { CurrencyEnum } from '@emico/graphql-schema-types'

import styles from './Slider.module.scss'
import { formatFilterPrice } from '../../catalog/common/ProductFilterPage/FilterBar/FilterBarDesktop'

interface OwnProps {
    field: string
    initialValue: number[]
    min: number
    max: number
    step?: number
    formatLabel?(
        value: number[],
        disabled: boolean,
        min: number,
        max: number,
        step: number,
    ): React.ReactNode
    allowCross?: boolean
    disabled?: boolean
    currency?: CurrencyEnum
}
export type Props = OwnProps & RangeProps

const generateList = (min: number, max: number, step: number) => {
    const a = []

    for (let i = min; i <= max; i += step) {
        a.push(i)
    }
    return a
}

const formatSliderLabel = (
    value: number[],
    min: number,
    max: number,
    step: number,
    disabled?: boolean,
    currency?: CurrencyEnum,
) => {
    const values = generateList(min, max, step)
    // Line height
    const valueHeight = 20

    const formattedMin = formatFilterPrice(value[0], currency)
    const formattedMax = formatFilterPrice(value[1], currency)

    const minOffset = values.indexOf(value[0]) * valueHeight * -1
    const maxOffset = values.indexOf(value[1]) * valueHeight * -1

    const minWidth = `${formattedMin.length}ch`
    const maxWidth = `${formattedMax.length}ch`

    return (
        <div className={cx(styles.valueLabel, { [styles.disabled]: disabled })}>
            <div className={cx(styles.valueScroll)}>
                <div
                    className={cx(styles.value, styles.min)}
                    style={{
                        transform: `translate3d(0, ${minOffset}px, 0)`,
                        width: minWidth,
                    }}
                >
                    {values.map((i, index) => (
                        <span key={`val-min-${index}`}>
                            {currency ? formatFilterPrice(i, currency) : i}
                        </span>
                    ))}
                </div>
            </div>
            <span className={styles.separator} />

            <div className={cx(styles.valueScroll)}>
                <div
                    className={cx(styles.value, styles.max)}
                    style={{
                        transform: `translate3d(0, ${maxOffset}px, 0)`,
                        width: maxWidth,
                    }}
                >
                    {values.map((i, index) => (
                        <span key={`val-max-${index}`}>
                            {currency ? formatFilterPrice(i, currency) : i}
                        </span>
                    ))}
                </div>
            </div>
        </div>
    )
}

interface BaseSliderProps extends RangeProps {
    value: number[]
    initialValue: number[]
    min: number
    max: number
    step?: number
    formatLabel?(
        value: number[],
        min: number,
        max: number,
        step: number,
        disabled: boolean,
        currency?: CurrencyEnum,
    ): React.ReactNode
    allowCross?: boolean
    disabled?: boolean
    currency?: CurrencyEnum
}

// A field-less variant
export const BaseSlider = ({
    value,
    initialValue,
    min,
    max,
    step = 1,
    disabled = false,
    allowCross = false,
    formatLabel = formatSliderLabel,
    currency,
    ...others
}: BaseSliderProps) => (
    // eslint-disable-next-line react/no-unknown-property
    <div className={styles.base} body-scroll-lock-ignore="true">
        {formatLabel(
            value !== undefined ? value : initialValue,
            min,
            max,
            step,
            disabled,
            currency,
        )}
        <Range
            allowCross={allowCross}
            min={min}
            max={max}
            step={step}
            defaultValue={initialValue}
            value={value}
            disabled={disabled}
            {...others}
        />
    </div>
)

const Slider = asField<Props, number[]>(
    ({
        fieldState: { value },
        fieldApi: { setValue },
        min,
        max,
        step = 1,
        allowCross = false,
        formatLabel = formatSliderLabel,
        disabled = false,
        ...others
    }) => (
        <BaseSlider
            allowCross={allowCross}
            onChange={setValue}
            min={min}
            max={max}
            step={step}
            value={value}
            disabled={disabled}
            {...others}
        />
    ),
)

export default Slider
